import { useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { FilterToolbarItem } from 'src/components/Filters';
import { useFilterQueryContext } from 'src/contexts/FilterQueryContext';
import { PosEnumMultiSelect } from 'src/core/POS/PosMultiSelect/PosEnumMultiSelect';
import { ContentId } from 'src/utils/constants/contentId';
import {
  COLLECTION_FILTER_OP_TO_CID,
  EVENT_STATUS_TO_CID,
} from 'src/utils/constants/contentIdMaps';
import { QueryWithViewMode } from 'src/utils/eventQueryUtils';
import {
  ApiEventStatus,
  EntityWithTicketsQuery,
  FilterOperationType,
} from 'src/WebApiController';

export type EventStateFilterQuery = Pick<
  EntityWithTicketsQuery,
  'eventStatuses'
> & {
  operations?: {
    eventStatusesOp?: FilterOperationType | null;
  };
};

export const useEventStateFilters = <TQuery extends EventStateFilterQuery>({
  supportMultiOperation = false,
  isHorizontalLayout = false,
}: {
  supportMultiOperation?: boolean;
  isHorizontalLayout?: boolean;
}): FilterToolbarItem[] => {
  const { tempQuery, setTempQuery } = useFilterQueryContext<
    TQuery & QueryWithViewMode
  >();

  const eventStatusToCidWithoutContingent = useMemo(() => {
    return (
      Object.entries(EVENT_STATUS_TO_CID)
        // Contingent status does not resolve to any valid events as a filter
        // See https://github.com/stubhub/StubHub.PointOfSale.Services/blob/680306b8d0a1d2046eaa8f7a77dd018d4b364105/src/StubHub.PointOfSale.Services.Implementation/Utils/EventUtils.cs#L17
        .filter(([key]) => key !== ApiEventStatus.Contingent)
        .reduce(
          (acc, [key, value]) => {
            acc[key as ApiEventStatus] = value;
            return acc;
          },
          {} as Record<ApiEventStatus, ContentId>
        )
    );
  }, []);

  return [
    {
      filterId: 'eventStatuses',
      labelContentId: ContentId.EventState,
      filterQueryKeys: ['eventStatuses'],
      filterItem: (
        <PosEnumMultiSelect
          triggerProps={{ style: { width: '100%' } }}
          values={tempQuery.eventStatuses || []}
          placeholderText={ContentId.All}
          enableEmptySelection
          operation={tempQuery.operations?.eventStatusesOp ?? null}
          operationOptionsContent={
            supportMultiOperation ? COLLECTION_FILTER_OP_TO_CID : undefined
          }
          isHorizontalLayout={isHorizontalLayout}
          onChange={({ newValues: eventStatuses, operation }) => {
            const isChanged =
              !isEqual(eventStatuses, tempQuery.eventStatuses) ||
              operation !== tempQuery.operations?.eventStatusesOp;

            if (isChanged) {
              setTempQuery({
                ...tempQuery,
                eventStatuses: eventStatuses ?? null,
                operations: supportMultiOperation
                  ? {
                      ...tempQuery.operations,
                      eventStatusesOp: operation,
                    }
                  : undefined,
              });
            }
          }}
          valueOptionsContent={eventStatusToCidWithoutContingent}
        />
      ),
    },
  ];
};
